import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import MagneticButton from "./MagneticButton";
import { useSectionParallax } from "../hooks/useAnimations";

const BG = "/images/neu/Pirate-Bowling-21.jpg";
const BG2 = "/images/neu/Pirate-Bowling-2.jpg";

export default function FinalCTA() {
  const { ref: parallaxRef, y: decoY } = useSectionParallax(70);

  return (
    <section
      ref={parallaxRef}
      id="cta"
      className="relative py-36 md:py-48 px-6 md:px-12 lg:px-20 overflow-hidden"
      data-testid="final-cta-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={BG}
          alt="Abenteuer Pirate Bowling"
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => { e.target.src = BG2; }}
        />
        <div className="absolute inset-0 bg-navy-deep/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy/40 to-navy-deep/80" />
        {/* Warm glow */}
        <div className="absolute inset-0 bg-gradient-to-tr from-gold/8 via-transparent to-transparent" />
      </div>

      {/* Decorative circles */}
      <motion.div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden" style={{ y: decoY }}>
        <div className="w-[700px] h-[700px] rounded-full border border-gold/28" />
        <div className="absolute w-[450px] h-[450px] rounded-full border border-gold/18" />
        <div className="absolute w-[200px] h-[200px] rounded-full border border-gold/12" />
      </motion.div>

      <div className="max-w-4xl mx-auto relative z-10 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="skull-divider mb-10"
          data-testid="cta-badge"
        >
          <span className="font-space-grotesk text-xs text-gold tracking-[0.3em] uppercase px-4">
            Bowling · Billard · Dart · Bar
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-unbounded text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-cream leading-tight mb-6"
          data-testid="cta-headline"
        >
          Bereit für dein
          <br />
          <span className="gold-gradient-text">nächstes Abenteuer?</span>
        </motion.h2>

        {/* Gold line */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          whileInView={{ scaleX: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mx-auto w-24 h-px bg-gradient-to-r from-transparent via-gold to-transparent mb-8"
        />

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.4 }}
          className="font-inter text-base md:text-lg text-cream/80 max-w-lg mx-auto mb-12 leading-relaxed"
          data-testid="cta-subtext"
        >
          Reserviere jetzt deine Bowling-Bahn und erlebe den besten Abend des Jahres im Pirate Bowling Center Velburg.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          data-testid="cta-buttons"
        >
          <MagneticButton>
            <a
              href="https://4bowl.de/pirate-bowling/online/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 btn-gold px-10 py-5 rounded-full text-sm"
              data-testid="cta-reserve-btn"
            >
              Bahn reservieren
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </MagneticButton>
          <MagneticButton>
            <a
              href="tel:+499182938936"
              className="inline-flex items-center gap-3 btn-outline px-10 py-5 rounded-full text-sm"
              data-testid="cta-contact-btn"
            >
              <Phone className="w-4 h-4" />
              Kontakt aufnehmen
            </a>
          </MagneticButton>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="font-inter text-xs text-cream/40 mt-8"
          data-testid="cta-address"
        >
          Hainstraße 4 · 92355 Velburg · Oberpfalz
        </motion.p>
      </div>
    </section>
  );
}
